import _ from "lodash";
import { v4 as uuidv4 } from "uuid";
import { ProductSelector, fabricApi } from "../imports";
import {
  getProductType,
  checkMultipleCanvas,
} from "../redux/creators/threekitCreators";
import OptionsUtility from "./OptionsUtility";
import { isFreeItem } from "./promoProduct";
import { hasFrontAndBack } from "./productHelpers";
import { getCurrentCanvas } from "./fabricHelpers";

export default class AddToCartUtility {
  constructor(state) {
    this.state = state;
    this.playerState = state.playerState;
    this.optionsUtility = new OptionsUtility({ state });
    this.selectedProduct = state.threekit.selectedProduct;
    this.productType = getProductType(state);
    this.canvas = getCurrentCanvas(state);
    this.quantity = state?.threekit.quantity || 1;
    this.variantOptions = state.threekit.variantOptions;
  }

  normalizedImageMagickObj = (
    imageMagickObj = this.playerState.imageMagickObj
  ) => {
    let obj = _.cloneDeep(imageMagickObj);
    Object.keys(obj).forEach((item) => {
      if (typeof obj[item] === "number") {
        obj[item] = obj[item].toString();
      }
    });
    return obj;
  };

  normalizedFinalObj = (finalObj = this.playerState.finalObj) => {
    let obj = _.cloneDeep(finalObj);
    let nodes = { ...obj.nodes };
    Object.keys(nodes).forEach((node) => {
      delete nodes[node].configurator;
      Object.keys(nodes[node]).forEach((nodeKey) => {
        if (typeof nodes[node][nodeKey] === "number") {
          nodes[node][nodeKey] = nodes[node][nodeKey].toString();
        }
      });
    });
    obj.nodes = nodes;
    return obj;
  };

  // Creates preview thumbnail image
  createPreviewThumbnail = async (canvas) => {
    canvas.discardActiveObject().renderAll();
    const image = await fabricApi.getSnapShotUrl();
    return image.image_url;
  };

  addToCart = () => {
    if (this.productType.includes("triptych")) {
      this.addTriptychToCart();
    } else if (checkMultipleCanvas(this.state)) {
      this.addMultipleCanvasToCart();
    } else {
      this.addSingleCanvasToCart();
    }
  };

  async addMultipleCanvasToCart() {
    const {
      playerState: { imageMagickObj, finalObj },
      extraOptions,
      optionsUtility,
      normalizedImageMagickObj,
      normalizedFinalObj,
      productType,
      variantOptions,
    } = this;

    // Clone object
    let newFinalObj = _.cloneDeep(finalObj);

    // Get nodes for product
    const nodes = Object.keys(finalObj.nodes);

    // Create thumbnail/preview screenshot
    const resultimage = await this.createPreviewThumbnail(this.canvas);

    // If front and back get both preview images
    if (hasFrontAndBack(productType)) {
      for (const node of nodes) {
        const canvas = getCurrentCanvas(this.state, node);
        canvas.discardActiveObject().renderAll();
        const image = await fabricApi.getMultipleSnapShotUrl(node);
        newFinalObj.nodes[node].resultimage = image.image_url;
      }
    }

    // Loop through each node
    for (const node of nodes) {
      // Get canvas
      const fabric = getCurrentCanvas(this.state, node);
      let newWidth, newHeight, canWidth, canHeight;

      // Get product dimensions for backend processing
      const { canWidth: printWidth, canHeight: printHeight } =
        this.state.playerState.dimensions;
      const dimensions = `${printHeight}x${printWidth}`;

      // Get new width/height values based on the actual fabric objects
      if (checkMultipleCanvas(this.state) && !hasFrontAndBack(productType)) {
        const group = fabric.item(0);
        const items = group._objects;
        const mask = items.find((obj) => obj.name == node);
        const image = items.find((obj) => obj.name == "img_" + node);
        newWidth = image.width * image.scaleX;
        newHeight = image.height * image.scaleY;
        canWidth = mask.width - 1;
        canHeight = mask.height - 1;
      } else {
        newWidth = fabric._objects[1].width * fabric._objects[1].scaleX;
        newHeight = fabric._objects[1].height * fabric._objects[1].scaleY;
        canWidth = fabric._objects[0].width;
        canHeight = fabric._objects[0].height;
      }
      newFinalObj.nodes[node] = {
        ...newFinalObj.nodes[node],
        imageHeight: newHeight,
        imageWidth: newWidth,
        canWidth,
        canHeight,
      };

      // If dimensions have not been set, set them here
      if (newFinalObj.nodes[node].dimensions === "0x0") {
        newFinalObj.nodes[node].dimensions = dimensions;
      }
    }

    // Get variant info for item properties
    let variantProperties;
    if (Object.keys(variantOptions).length > 0) {
      const variantOptionTypes = variantOptions.map((option) => option.name);
      variantProperties = Object.fromEntries(
        variantOptionTypes.map((key, index) => [
          key,
          this.selectedProduct.options[index],
        ])
      );
    }

    // Set properties for item
    const properties = {
      ...optionsUtility.optionsForCart,
      result_image: resultimage,
      _imageMagick: {
        ...normalizedImageMagickObj(imageMagickObj),
        ...normalizedFinalObj(newFinalObj),
        resultimage,
      },
      ...extraOptions,
      ...variantProperties,
    };

    // Send item to cart
    this.sendToCart(properties);
  }

  async addTriptychToCart() {
    const {
      playerState: { imageMagickObj, finalObj },
      extraOptions,
      optionsUtility,
      normalizedImageMagickObj,
    } = this;
    let newImageMagickObj = _.cloneDeep(imageMagickObj);
    let newFinalObj = _.cloneDeep(finalObj);

    // Create thumbnail/preview screenshot
    const resultimage = await this.createPreviewThumbnail(this.canvas);

    const fabric = this.canvas;
    const maskGroup = fabric.item(0);
    const imageGroup = fabric.item(1);

    // Get new width/height values based on the actual fabric objects
    const image = imageGroup._objects[1];
    const imageWidth = image.width;
    const imageHeight = image.height;
    const newWidth = imageWidth * image.scaleX;
    const newHeight = imageHeight * image.scaleY;
    const canWidth = maskGroup.width - 31;
    const canHeight = maskGroup.height - 1;

    const heightRatio = canHeight / imageHeight;
    const widthRatio = canWidth / imageWidth;

    let imgHeight, imgWidth;
    if (heightRatio > widthRatio) {
      imgHeight = imageHeight * heightRatio;
      imgWidth = imageWidth * heightRatio;
    } else {
      imgHeight = imageHeight * widthRatio;
      imgWidth = imageWidth * widthRatio;
    }

    delete newFinalObj.canHeight;
    delete newFinalObj.canWidth;

    // Get product dimensions for backend processing
    const dimension = finalObj.metadata["_size"].split("_");
    const dimensions = `${dimension[0]}x${dimension[1]}`;

    // Set new values
    newFinalObj = {
      ...newFinalObj,
      centerHeight: maskGroup._objects[1].height,
      centerWidth: maskGroup._objects[1].width,
      sideHeight: maskGroup._objects[0].height,
      sideWidth: maskGroup._objects[0].width,
    };
    newImageMagickObj = {
      ...newImageMagickObj,
      imageHeight: newHeight,
      imageWidth: newWidth,
      canWidth,
      canHeight,
      dimensions,
      imageHeightCal: imgHeight,
      imageWidthCal: imgWidth,
    };

    // Set properties for item
    const properties = {
      ...optionsUtility.optionsForCart,
      result_image: resultimage,
      _imageMagick: {
        ...normalizedImageMagickObj(newImageMagickObj),
        ...newFinalObj,
        resultimage,
      },
      ...extraOptions,
    };

    // Send item to cart
    this.sendToCart(properties);
  }

  async addSingleCanvasToCart() {
    const {
      playerState: {
        imageMagickObj,
        finalObj,
        dimensions: { canHeight, canWidth },
      },
      extraOptions,
      optionsUtility,
      normalizedImageMagickObj,
      variantOptions,
    } = this;

    // Create thumbnail/preview screenshot
    const resultimage = await this.createPreviewThumbnail(this.canvas);

    // Clone objects
    let newImageMagickObj = _.cloneDeep(imageMagickObj);
    let newFinalObj = _.cloneDeep(finalObj);

    // Calculate ratio
    const { imageHeight, imageWidth } = imageMagickObj;
    const calculateRatio = () => {
      return imageHeight > imageWidth
        ? imageHeight / (canHeight * 96)
        : imageWidth / (canWidth * 96);
    };

    // Get product dimensions for backend processing
    let printWidth = this.state.playerState.dimensions.canWidth;
    let printHeight = this.state.playerState.dimensions.canHeight;
    let dimensions = `${printHeight}x${printWidth}`;
    let selectedVariant_id = this.state.threekit.selectedProduct.id;
    let selectedVariants = this.state.threekit.selectedProduct.variants.filter(
      (variant) => variant.id == selectedVariant_id
    );

    !selectedVariants.length &&
      (selectedVariants = this.state.threekit.selectedProduct.variants);

    let sku_dimensions = selectedVariants[0].sku.split("-")[2].toLowerCase();

    const overwrite_dimension_with_sku = () => {
      dimensions = sku_dimensions;
      printHeight = dimensions.split("x")[0];
      printWidth = dimensions.split("x")[1];
    };

    // If dimensions are 0 set from SKU
    if (printHeight === 0 || printWidth === 0) {
      overwrite_dimension_with_sku();
    }

    // If sku includes an x and doesn't match current dimensions
    if (
      sku_dimensions.includes("x") &&
      (sku_dimensions.split("x")[0] != printHeight ||
        sku_dimensions.split("x")[1] != printWidth)
    ) {
      overwrite_dimension_with_sku();
    }

    // Plaque dimensions are unique, set them here
    if (this.productType.includes("plaque")) {
      let print_plaque_h = parseFloat(printHeight) - 1.1875;
      let print_plaque_w = parseFloat(printWidth) - 1.1875;
      dimensions = `${print_plaque_h}x${print_plaque_w}`;
    }

    // Get new width/height values based on the actual fabric objects
    const fabric = this.canvas;
    const newWidth = fabric._objects[1].width * fabric._objects[1].scaleX;
    const newHeight = fabric._objects[1].height * fabric._objects[1].scaleY;
    const newCanWidth = fabric._objects[0].width;
    const newCanHeight = fabric._objects[0].height;
    newImageMagickObj = {
      ...newImageMagickObj,
      imageHeight: newHeight,
      imageWidth: newWidth,
      canWidth: newCanWidth,
      canHeight: newCanHeight,
      dimensions,
    };
    delete newFinalObj.canHeight;
    delete newFinalObj.canWidth;

    // Get variant info for item properties
    let variantProperties;
    if (Object.keys(variantOptions).length > 0) {
      const variantOptionTypes = variantOptions.map((option) => option.name);
      variantProperties = Object.fromEntries(
        variantOptionTypes.map((key, index) => [
          key,
          this.selectedProduct.options[index],
        ])
      );
    }

    // Set properties for item
    const properties = {
      ...optionsUtility.optionsForCart,
      result_image: resultimage,
      _imageMagick: {
        ...normalizedImageMagickObj(newImageMagickObj),
        ...newFinalObj,
        ratio: calculateRatio(),
        zoom: newFinalObj.ratio,
        resultimage,
      },
      ...extraOptions,
      ...variantProperties,
    };

    // Send item to cart
    this.sendToCart(properties);
  }

  sendToCart = (properties) => {
    const linkId = uuidv4();
    const queryParams = new URLSearchParams(window.location.search);
    const purl = queryParams.get("purl") || localStorage.getItem("purl") || "";
    delete localStorage.purl;

    // Custom properties
    properties["_linkId"] = linkId;
    properties["_purl"] = purl;
    properties["_intuitive_cid"] = customerId;

    /**
     * Add promo property to _imageMagick object to signal if item
     * is a valid promo item from free.canvasprints.com
     */
    properties["_imageMagick"].promo =
      isFreeItem(this.selectedProduct) || false;

    let productId =
      Object.keys(this.variantOptions).length > 0
        ? this.selectedProduct.id
        : this.selectedProduct.variants[0].id;

    const productDetails = {
      properties,
      id: productId,
      quantity: this.quantity,
    };

    let cartDetails = [
      productDetails,
      ...this.optionsUtility.customProductsForCart(linkId, this.quantity),
    ];

    ProductSelector.addToCart(
      cartDetails,
      () => (window.location.href = "/cart"),
      () => alert("Something went wrong. We apologize for the inconvenience.")
    );
  };
}
